/**
 * Seção "Exposição" da janela de dados do risco (#606, E2): nível, atividade, período, matriz e
 * observações. A matriz é a do cadastro do risco: tipo, probabilidade e severidade se escolhem nos
 * campos, e a grade colorida ao lado destaca a célula resultante como apoio visual.
 */

import React from 'react';
import {
  Box,
  FormHelperText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Controller, useFormContext } from 'react-hook-form';
import { ExposureLevel, ExposureLevelLabels } from '../../../types/riskApplication';
import type { FormularioDoRisco } from '../RiskApplicationModal';
import { ALVO } from '../completudeDoRisco';
import { RiskMatrixStep } from '../../../pages/occupational-risks/components/steps/RiskMatrixStep';
import { RotuloQueConta } from './comum';

const O_QUE_O_NIVEL_QUER_DIZER: Record<ExposureLevel, string> = {
  [ExposureLevel.LOW]: 'Baixa: exposição ocasional ou em baixa concentração.',
  [ExposureLevel.MEDIUM]: 'Média: o trabalhador fica exposto durante parte da jornada.',
  [ExposureLevel.HIGH]: 'Alta: exposição frequente, próxima aos limites de tolerância.',
  [ExposureLevel.VERY_HIGH]: 'Muito alta: exposição contínua, acima dos limites de tolerância.',
};

export const SecaoExposicao: React.FC<{ desabilitada: boolean }> = ({ desabilitada }) => {
  const { control, watch, formState } = useFormContext<FormularioDoRisco>();
  const { errors } = formState;
  const nivel = watch('exposureLevel');

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          <RotuloQueConta texto="Nível de exposição" />
        </Typography>
        <Controller
          name="exposureLevel"
          control={control}
          render={({ field }) => (
            <ToggleButtonGroup
              id={ALVO.nivel}
              exclusive
              color="primary"
              value={field.value}
              disabled={desabilitada}
              onChange={(_, valor: ExposureLevel | null) => valor && field.onChange(valor)}
              aria-label="Nível de exposição"
            >
              {Object.values(ExposureLevel).map((opcao) => (
                <ToggleButton key={opcao} value={opcao} sx={{ minHeight: 44, px: 2, textTransform: 'none' }}>
                  {ExposureLevelLabels[opcao]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        />
        <FormHelperText>{nivel ? O_QUE_O_NIVEL_QUER_DIZER[nivel] : ''}</FormHelperText>
      </Box>

      <Controller
        name="activityProcess"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            id={ALVO.atividade}
            label={<RotuloQueConta texto="Atividade / processo" />}
            fullWidth
            multiline
            minRows={2}
            disabled={desabilitada}
            error={!!errors.activityProcess}
            helperText={errors.activityProcess?.message ?? 'O que o trabalhador faz quando fica exposto.'}
          />
        )}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Controller
          name="startDate"
          control={control}
          render={({ field }) => (
            <DatePicker
              {...field}
              label={<RotuloQueConta texto="Data de início" />}
              disabled={desabilitada}
              maxDate={new Date()}
              slotProps={{
                textField: {
                  id: ALVO.inicio,
                  fullWidth: true,
                  error: !!errors.startDate,
                  helperText: errors.startDate?.message ?? 'Quando o trabalhador começou a ser exposto.',
                },
              }}
            />
          )}
        />
        <Controller
          name="endDate"
          control={control}
          render={({ field }) => (
            <DatePicker
              {...field}
              label="Data de fim"
              disabled={desabilitada}
              slotProps={{
                field: { clearable: true },
                textField: {
                  fullWidth: true,
                  error: !!errors.endDate,
                  helperText: errors.endDate?.message ?? 'Opcional. Deixe vazio enquanto a exposição continua.',
                },
              }}
            />
          )}
        />
      </Stack>

      <Box id={ALVO.matriz}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
          <RotuloQueConta texto="Matriz de risco" />
        </Typography>
        <RiskMatrixStep
          disabled={desabilitada}
          ids={{ tipo: ALVO.tipoDaMatriz, probabilidade: ALVO.probabilidade, severidade: ALVO.severidade }}
        />
      </Box>

      <Controller
        name="observations"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Observações"
            fullWidth
            multiline
            minRows={2}
            disabled={desabilitada}
            error={!!errors.observations}
            helperText={errors.observations?.message}
          />
        )}
      />
    </Stack>
  );
};

export default SecaoExposicao;
